import { useEffect, useState, useCallback } from "react";
import { Search, Trash2, Phone, Mail, Inbox, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDate } from "@/lib/util";
import { telHref } from "@/context/SettingsContext";
import { PageTitle, inputCls } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["ALL", "NEW", "CONTACTED", "CLOSED"];
const COLORS = { NEW: "bg-blue-100 text-blue-700", CONTACTED: "bg-amber-100 text-amber-700", CLOSED: "bg-green-100 text-green-700" };

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status !== "ALL") p.set("status", status);
    if (search) p.set("search", search);
    api.get(`/enquiries?${p}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [status, search]);
  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

  const updateStatus = async (id, newStatus) => {
    await api.put(`/enquiries/${id}`, { status: newStatus });
    toast.success("Status updated");
    load();
    if (selected?.id === id) setSelected({ ...selected, status: newStatus });
  };
  const saveNotes = async (id, notes) => { await api.put(`/enquiries/${id}`, { notes }); toast.success("Notes saved"); };
  const del = async (id) => { if (!window.confirm("Delete this enquiry?")) return; await api.delete(`/enquiries/${id}`); toast.success("Deleted"); setSelected(null); load(); };

  return (
    <div data-testid="admin-enquiries">
      <PageTitle title="Enquiries" subtitle="Client leads submitted through the contact form" />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-4 py-2 rounded-sm text-sm font-medium ${status === s ? "bg-navy text-ivory" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`filter-${s.toLowerCase()}`}>{s}</button>
          ))}
        </div>
        <div className="relative flex-1 sm:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${inputCls} pl-9`} placeholder="Search name, phone, email..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="enquiry-search" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? <div className="py-16 text-center text-slate-400">Loading…</div> :
          items.length === 0 ? <div className="py-16 text-center text-slate-400"><Inbox size={36} className="mx-auto mb-3 text-slate-300" />No enquiries received.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ivory-dark text-slate-500 text-xs uppercase tracking-wide">
                <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3 hidden sm:table-cell">Matter</th><th className="text-left px-4 py-3 hidden md:table-cell">Date</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-ivory-dark/40 cursor-pointer" onClick={() => setSelected(e)} data-testid={`enquiry-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-navy font-medium">{e.name}</p>
                      <p className="text-slate-400 text-xs">{e.phone}</p>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-slate-600">{e.subject}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-400 text-xs">{formatDate(e.created_at)}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-wide ${COLORS[e.status]}`}>{e.status}</span></td>
                    <td className="px-4 py-3 text-right"><button onClick={(ev) => { ev.stopPropagation(); del(e.id); }} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="enquiry-detail">
          <DialogHeader><DialogTitle>Enquiry Details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div><p className="text-xs text-slate-400 uppercase">Name</p><p className="text-navy font-medium">{selected.name}</p></div>
              <div className="flex gap-6">
                <a href={telHref(selected.phone)} className="flex items-center gap-2 text-navy hover:text-brass"><Phone size={15} className="text-brass" />{selected.phone}</a>
                {selected.email && <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-navy hover:text-brass"><Mail size={15} className="text-brass" />{selected.email}</a>}
              </div>
              <div><p className="text-xs text-slate-400 uppercase">Nature of Matter</p><p className="text-navy">{selected.subject}</p></div>
              <div><p className="text-xs text-slate-400 uppercase">Preferred Contact</p><p className="text-navy">{selected.preferred_contact}</p></div>
              <div><p className="text-xs text-slate-400 uppercase">Message</p><p className="text-slate-600 whitespace-pre-line">{selected.message}</p></div>
              <div><p className="text-xs text-slate-400 uppercase mb-1">Status</p>
                <div className="flex gap-2">
                  {["NEW", "CONTACTED", "CLOSED"].map((s) => (
                    <button key={s} onClick={() => updateStatus(selected.id, s)} className={`px-3 py-1.5 rounded-sm text-xs font-medium ${selected.status === s ? "bg-navy text-ivory" : "bg-slate-100 text-slate-600"}`} data-testid={`set-status-${s.toLowerCase()}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div><p className="text-xs text-slate-400 uppercase mb-1">Internal Notes</p>
                <textarea rows={3} className={inputCls} defaultValue={selected.notes} onBlur={(e) => saveNotes(selected.id, e.target.value)} placeholder="Add private notes..." data-testid="enquiry-notes" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
